import type { ReactNode } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import ArticleHero from '../../../../components/news/ArticleHero';
import PullQuote from '../../../../components/news/PullQuote';
import KeyPointsBox from '../../../../components/news/KeyPointsBox';
import CommentBox from '../../../../components/news/CommentBox';
import SourceCard from '../../../../components/news/SourceCard';
import RelatedCards from '../../../../components/news/RelatedCards';

/* ---- In-article inline parts (editorial layout; no section bands) ---- */

function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}

function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        Source: {media} (<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>)
      </figcaption>
    </figure>
  );
}

function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}

const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function TibaGlobalAwardFiveYearsPageEN() {
  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'News', href: '/en/news' },
    { label: 'TIBA Wins Global Award for Fifth Consecutive Year' }
  ];

  const article = {
    category: 'Education & Industry Authority',
    date: 'December 20, 2024',
    title: 'TIBA Wins Global Award for Fifth Consecutive Year',
    subtitle: "Recognized as World's Highest Standard Butler Training Institution",
    heroImage: '/images/generated/jba-fs-132-news_tiba-global-award-five-years.webp',
    lead: "The Netherlands-based International Butler Academy (TIBA) has been recognized as the Best Butler Training Institution for five consecutive years at the Corporate Livewire Global Awards.\n\nTIBA's success with high graduation and employment rates is an important case demonstrating international standardization of butler education and the value of specialized education.",
    comment: 'The International Butler Academy\'s (TIBA) five consecutive global awards are deeply respected by our association as an important milestone in international standardization of butler education.\n\nThe value of specialized education proven by high graduation and employment rates is an important indicator demonstrating the expertise and economic value of the butler profession to the world.\n\nThe role of educational institutions in global butler talent development contributes to inheriting and developing butler culture as an art of service, beyond mere skill acquisition.\n\nTIBA\'s continued success shows that butler education is recognized as an established academic field, providing significant suggestions about the development potential of butler education in Japan.\n\nWe will make even greater efforts toward establishing butler education programs that comply with international educational standards while fusing with Japan\'s unique "omotenashi" culture.',
    related: [
      { to: '/en/news/grant-harrold-royal-butler-memoir', image: '/images/generated/jba-fs-en-022-news_tiba-global-award-five-years.webp', aspect: 'aspect-[3/4]', alt: 'Related article: Grant Harrold Royal Butler Memoir Published', title: 'Grant Harrold Royal Butler Memoir Published', desc: 'Former butler to King Charles shares royal experiences' },
      { to: '/en/news/raffles-london-campaign', image: '/images/generated/seq-london-related-6ec8d4.webp', aspect: 'aspect-[3/4]', alt: 'Related article: Raffles London hotel setting for the campaign sequel', title: 'Raffles "The Butler Did It" Campaign Chapter 2', desc: 'Luxury hotel butler service innovation' }
    ]
  };

  const sources = [
    { label: 'Source', value: 'The International Butler Academy official website', link: false },
    { label: 'Official Site', value: 'https://www.butlerschool.com/', link: true },
    { label: 'Placement & Outcomes', value: 'https://www.butlerschool.com/recruitment-agency/finding-work-and-internships/', link: true },
    { label: 'Award (CFI)', value: 'https://cfi.co/awards/europe/2020/the-international-butler-academy-best-private-butler-training-global-2020/', link: true },
    { label: 'Access Date', value: 'July 2026', link: false }
  ];

  const toc = [
    { id: 's-org', label: 'Organization Overview and Award History' },
    { id: 's-accolades', label: 'Global Accolades in Detail' },
    { id: 's-program', label: 'Training Program Features and Results' },
    { id: 's-philosophy', label: 'Educational Philosophy' },
    { id: 's-network', label: 'Graduate Outcomes and Network' },
    { id: 's-global', label: 'Global Expansion and Industry Impact' }
  ];

  return (
    <Layout>
      <title>TIBA Wins Global Award for Fifth Consecutive Year | Japan Butler Association</title>
      <meta name="description" content="The Netherlands-based International Butler Academy is recognized as the Best Butler Training Institution for five consecutive years, with a 95% graduation rate and 85% employment rate." />
      <link rel="canonical" href="https://butler-association.jp/en/news/tiba-global-award-five-years" />
      <link rel="alternate" hrefLang="ja" href="https://butler-association.jp/news/tiba-global-award-five-years" />
      <link rel="alternate" hrefLang="en" href="https://butler-association.jp/en/news/tiba-global-award-five-years" />

      <ArticleHero eyebrow="News / Education" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <div className="space-y-4">
              {article.lead.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean).map((s, i) => (
                <p key={i} className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{s}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="Article Highlights"
              points={[
                'TIBA was named "The Best Butler School in the World" by Capital Finance International for 2019 and 2020, and has won a Corporate Livewire Global Award every year from 2021 through 2025',
                'A 95% graduation rate and 85% employment rate; graduates work in royal households, private homes, hotels, embassies, cruise ships, super yachts and Fortune 500 companies',
                'Founded in 1999 by Robert Wennekes in Simpelveld, Netherlands, TIBA guarantees job placement for graduates scoring 900 points or more'
              ]}
            />

            {/* 01: Overview (existing body, unchanged) */}
            <SectionHeading id="s-org" en="Overview">Organization Overview and Award History</SectionHeading>
            <div className="space-y-6">
              <p className={P}>The International Butler Academy (TIBA) is a world-class butler training institution based in the Netherlands. With the motto "Welcome to the world's best butler school," it trains service excellence professionals.</p>
              <div className="space-y-5">
                <p className={P}>TIBA has been recognized as the Best Butler Training Institution for five consecutive years (2021-2025) at the Corporate Livewire Global Awards, and is also recognized as "The World's Best Butler Training Institution" by Capital Finance International.</p>
                <p className={P}>This continuous recognition establishes a firm position in the industry.</p>
              </div>
            </div>
            <SourceShot
              src="/images/news-sources/tiba-global-award-five-years-butlerschool.jpg"
              alt="Screenshot of the homepage of The International Butler Academy (TIBA) official website, showing its awards and motto"
              media="The International Butler Academy official site"
              url="https://www.butlerschool.com/"
            />

            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="Welcome to the world's best butler school. To a world of service excellence." cite="The International Butler Academy" />
            </div>

            {/* Augmented: Global Accolades */}
            <SectionHeading id="s-accolades" en="Global Accolades">Global Accolades in Detail</SectionHeading>
            <div className="space-y-6">
              <div className="space-y-5">
                <p className={P}>TIBA was awarded the title of "The Best Butler School in the World" by Capital Finance International for <Stat>2019</Stat> and <Stat>2020</Stat>.</p>
                <p className={P}>It also received the Global Award from Corporate Livewire for "Specialist Education & Training Provider of the Year" for <Stat>5</Stat> consecutive years, from 2021 through 2025.</p>
              </div>
              <p className={P}>This sustained recognition by independent third-party bodies is evidence that TIBA has upheld industry standards over the long term, not just in a single year.</p>
            </div>
            <SourceShot
              src="/images/news-sources/tiba-global-award-five-years-cfi.jpg"
              alt="Screenshot of the Capital Finance International (CFI.co) award page featuring The International Butler Academy"
              media="CFI.co (Capital Finance International)"
              url="https://cfi.co/awards/europe/2020/the-international-butler-academy-best-private-butler-training-global-2020/"
            />

            {/* 02: Program (existing body, unchanged) */}
            <SectionHeading id="s-program" en="Program">Training Program Features and Results</SectionHeading>
            <div className="space-y-6">
              <p className={P}>TIBA's training program is designed as an 8-week intensive training, achieving very high results. It boasts excellent performance with a 95% graduation rate, 85% employment rate, and high average graduate salaries.</p>
              <p className={P}>The curriculum includes classic butler service techniques, wine service and sommelier skills, table setting and dining etiquette, international etiquette and protocol, modern household management skills, and luxury hospitality services.</p>
            </div>

            {/* 03: Philosophy (existing body, unchanged) */}
            <SectionHeading id="s-philosophy" en="Philosophy">Educational Philosophy and Transformative Learning Experience</SectionHeading>
            <div className="space-y-6">
              <p className={P}>TIBA upholds the philosophy "Welcome to a world of style, elegance, and courtesy. To a world of service excellence. This is a school for butlers, where dreams take flight and professionalism is nurtured."</p>
              <div className="space-y-5">
                <p className={P}>It provides a transformative journey where every detail matters, etiquette and precision intertwine, and the true art of service becomes a lifestyle.</p>
                <p className={P}>Graduates are prepared to open doors to prestigious opportunities and build lasting legacies in the butler world.</p>
              </div>
            </div>

            {/* Augmented: Graduate Outcomes */}
            <SectionHeading id="s-network" en="Graduate Outcomes">Graduate Outcomes and Network</SectionHeading>
            <div className="space-y-6">
              <p className={P}><Stat>95</Stat>% of students graduate successfully with a diploma, and <Stat>85</Stat>% of graduates hold outstanding positions in royal households, private homes, hotels, embassies, cruise ships, super yachts and Fortune 500 companies.</p>
              <p className={P}>Based in Simpelveld, Netherlands, TIBA has built an extensive network with prominent employers worldwide, and this network is the foundation for consistently producing high-quality talent.</p>
            </div>

            {/* 04: Global Expansion (existing body, unchanged) */}
            <SectionHeading id="s-global" en="Global Reach">Global Expansion and Industry Impact</SectionHeading>
            <div className="space-y-6">
              <p className={P}>TIBA has international recognition and accepts students from around the world. Graduates work in luxury hotels, private residences, superyachts, and royal households across Europe, the Middle East, Asia, and the Americas.</p>
              <div className="space-y-5">
                <p className={P}>Five consecutive years of awards show that TIBA continues to establish industry standards. This continuous recognition becomes an important selection criterion for candidates considering butler training, further enhancing TIBA's brand value.</p>
                <p className={P}>High graduation and employment rates prove the high quality of TIBA's programs and that graduates are valued as immediate assets in the industry.</p>
              </div>
            </div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="Contents" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">Base</dt><dd className="text-[#333] mt-0.5">Simpelveld, Netherlands</dd></div>
                  <div><dt className="text-[#8f7a56]">Founded</dt><dd className="text-[#333] mt-0.5">1999 (Robert Wennekes)</dd></div>
                  <div><dt className="text-[#8f7a56]">Awards</dt><dd className="text-[#333] mt-0.5">5 years running (2021-2025)</dd></div>
                  <div><dt className="text-[#8f7a56]">Category</dt><dd className="text-[#333] mt-0.5">{article.category}</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="Comment from Japan Butler Association" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="Source" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="Related Articles" items={article.related} readLabel="Read" backHref="/en/news" backLabel="Back to News List" />
    </Layout>
  );
}
